import { StorageInterface } from '../stores/childNavStores';
import { 
  AdminStateDto, 
  AdminDataDto, 
  AdminPreFillDto, 
  AdminBackTableDto,
  AdminAssessmentSession,
  AdminSegmentNavigation,
  AdminStateStorageQuery,
  AdminStateSyncRequest,
  AdminStateSyncResponse
} from '../types/adminState';
import { AppProfilePHIDto } from '../types/api/appProfile';
import { calculateAgeInMonths } from '../types/profile';

/**
 * AdminState Storage Service - Handles AdminState management for assessments
 */
export class AdminStateStorage {
  private storage: StorageInterface;
  private cache: Map<string, AdminStateDto> = new Map();
  private sessionCache: Map<string, AdminAssessmentSession> = new Map();

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  /**
   * Create new AdminState for a profile/assessment
   */
  async createAdminState(
    accountId: string,
    profileId: string,
    surveyId: string,
    sourceId: string,
    segmentList: string,
    languageTypeId: string = '1',
    assessmentName?: string,
    surveyAliasId?: string,
    phi?: AppProfilePHIDto
  ): Promise<AdminStateDto> {
    const now = new Date().toISOString();
    
    // Use first segment from segmentList
    const segments = segmentList.split(',').map(s => s.trim()).filter(s => s);
    const firstSegment = segments.length > 0 ? segments[0] : '1';
    
    const adminState: AdminStateDto = {
      accountId: '',       // Populated from session user on submission
      profileId,
      pageNum: '1',
      node: '',            // Current node name/id (backend field, updated during node navigation)
      segment: firstSegment, // Start with first segment from segmentList
      segmentList,
      modified: now,
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      surveyId,
      sourceId,
      surveyAliasId: surveyAliasId || sourceId, // Default to sourceId if not provided
      dName: assessmentName || `Assessment_${sourceId}`,
      languageTypeId,
      launchqueueId: '',
      patEvntId: '',
      patRefId: '',
      recId: '',  // Blank until the backend confirms submission and returns a real RecId
      status: 'IN_PROGRESS',
      userId: '',          // Populated from session user on submission
      accessType: '',      // Populated on submission (default '5')
      phi,
      data: (() => {
        if (!phi?.dob) return [];
        const dob = new Date(phi.dob);
        if (isNaN(dob.getTime())) return [];
        const msPerDay = 24 * 60 * 60 * 1000;
        const ageday = Math.floor((Date.now() - dob.getTime()) / msPerDay);
        const agemonth = calculateAgeInMonths(phi.dob);
        return [
          { name: 'ageday',   value: String(ageday),   segment: '1', pageNum: '1' },
          { name: 'agemonth', value: String(agemonth), segment: '1', pageNum: '1' },
        ] as AdminDataDto[];
      })(),
      prefill: [],
      backTable: []
    };

    await this.saveAdminState(adminState);
    console.log(`✅ Created AdminState: ${adminState.recId} for profile ${profileId}`);
    
    return adminState;
  }

  /**
   * Save AdminState to storage
   */
  async saveAdminState(adminState: AdminStateDto): Promise<void> {
    try {
      const key = this.generateKeyForAdminState(adminState);
      
      // Update modified timestamp
      adminState.modified = new Date().toISOString();
      
      const jsonData = JSON.stringify(adminState);
      
      // Save to storage
      await this.storage.setItem(key, jsonData);
      
      // Update cache
      this.cache.set(key, adminState);
      
      // Update key registry
      await this.updateKeyRegistry(key);
      
    } catch (error) {
      console.error('Failed to save AdminState:', error);
      throw error;
    }
  }

  /**
   * Load AdminState from storage
   */
  async loadAdminState(
    profileId: string,
    sourceId: string
  ): Promise<AdminStateDto | null> {
    try {
      // First try to find the most recent AdminState for this assessment
      const allKeys = await this.getAllAdminStateKeys();
      const matchingKeys = allKeys.filter(key => 
        key.startsWith(`admin_state_${profileId}_${sourceId}`)
      );
      
      // If we have multiple matching keys (new format with recId), find the most recent
      if (matchingKeys.length > 0) {
        let mostRecentState: AdminStateDto | null = null;
        let mostRecentModified = '';
        
        for (const key of matchingKeys) {
          const stored = await this.storage.getItem(key);
          if (stored) {
            try {
              const state: AdminStateDto = JSON.parse(stored);
              if (!mostRecentState || state.modified > mostRecentModified) {
                mostRecentState = state;
                mostRecentModified = state.modified;
              }
            } catch (parseError) {
              console.error('Failed to parse AdminState:', parseError);
            }
          }
        }
        
        if (mostRecentState) {
          return mostRecentState;
        }
      }
      
      // Fallback to old key format for backward compatibility
      const key = this.generateKey(profileId, sourceId);
    
      // Check cache first
      if (this.cache.has(key)) {
        const cachedState = this.cache.get(key)!;
        return cachedState;
      }
      
      // Load from storage
      const stateJson = await this.storage.getItem(key);
      
      if (stateJson) {
        const adminState = JSON.parse(stateJson) as AdminStateDto;
        this.cache.set(key, adminState);
        return adminState;
      }
      
      return null;
    } catch (error) {
      console.log('Failed to load AdminState:', error);
      return null;
    }
  }

  /**
   * Query AdminStates by criteria
   */
  async queryAdminStates(query: AdminStateStorageQuery): Promise<AdminStateDto[]> {
    try {
      const allKeys = await this.getAllAdminStateKeys();
      const results: AdminStateDto[] = [];
      
      for (const key of allKeys) {
        const stateJson = await this.storage.getItem(key);
        if (stateJson) {
          const adminState = JSON.parse(stateJson) as AdminStateDto;
          
          // Apply query filters
          if (query.profileId && adminState.profileId !== query.profileId) continue;
          if (query.surveyId && adminState.surveyId !== query.surveyId) continue;
          if (query.sourceId && adminState.sourceId !== query.sourceId) continue;
          if (query.status && adminState.status !== query.status) continue;
          
          results.push(adminState);
        }
      }
      
      return results.sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime());
    } catch (error) {
      console.error('Failed to query AdminStates:', error);
      return [];
    }
  }

  /**
   * Update answer data in AdminState
   */
  async updateAnswerData(
    accountId: string,
    profileId: string,
    sourceId: string,
    mvarName: string,
    value: string,
    segment?: string,
    pageNum?: string,
    nodeIndex?: number
  ): Promise<void> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    if (!adminState) {
      throw new Error('AdminState not found');
    }

    // Use current segment/pageNum if not provided
    const currentSegment = segment || adminState.segment;
    const currentPageNum = pageNum || adminState.pageNum;

    // Find existing data entry by name only (treat Name as unique primary key)
    const existingIndex = adminState.data.findIndex(
      data => data.name === mvarName
    );

    const dataEntry: AdminDataDto = {
      name: mvarName,
      value,
      segment: currentSegment,
      pageNum: currentPageNum,
      ...(nodeIndex !== undefined ? { nodeIndex } : {})
    };

    // Update existing entry or add new one (Name is unique primary key)
    if (existingIndex >= 0) {
      // Update existing entry - override Value, Segment, PageNum
      adminState.data[existingIndex] = dataEntry;

    } else {
      // Add new entry
      adminState.data.push(dataEntry);

    }

    // NOTE: BackTable is managed exclusively by AdminView for navigation purposes
    // BackTable uses page names (AdminPageDto.name), Data uses mvar names (AdminQuestionDto.mvarName)
    // Do NOT manipulate BackTable here - it should only contain navigation history with page names

    await this.saveAdminState(adminState);
  }

  /**
   * Get answer data value - checks Data first, then falls back to Prefill
   */
  getAnswerData(adminState: AdminStateDto, mvarName: string, segment?: string): string | null {
    // First check if there's a current answer in the Data array (Name is unique primary key)
    const dataEntry = adminState.data.find(data => data.name === mvarName);
    
    if (dataEntry) {
      return dataEntry.value;
    }
    
    // If no current answer, check Prefill array for pre-selected value
    const prefillEntry = adminState.prefill.find(prefill => prefill.name === mvarName);
    return prefillEntry?.value || null;
  }

  /**
   * Get prefill value for a question MVar
   */
  getPrefillValue(adminState: AdminStateDto, mvarName: string): string | null {
    const prefillEntry = adminState.prefill.find(prefill => prefill.name === mvarName);
    return prefillEntry?.value || null;
  }

  /**
   * Add or update prefill data
   */
  async updatePrefillData(
    accountId: string,
    profileId: string,
    sourceId: string,
    mvarName: string,
    value: string
  ): Promise<void> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    if (!adminState) {
      throw new Error('AdminState not found');
    }

    const existingIndex = adminState.prefill.findIndex(prefill => prefill.name === mvarName);
    const prefillEntry: AdminPreFillDto = { name: mvarName, value };

    if (existingIndex >= 0) {
      adminState.prefill[existingIndex] = prefillEntry;
    } else {
      adminState.prefill.push(prefillEntry);
    }

    await this.saveAdminState(adminState);
  }

  /**
   * Navigate to next page within current segment or to next segment
   */
  async navigateToNextPage(
    profileId: string,
    sourceId: string,
    totalPagesInCurrentSegment: number
  ): Promise<AdminStateDto | null> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    if (!adminState) return null;

    const currentPage = parseInt(adminState.pageNum) || 1;
    
    // Add current page to BackTable before navigation (for previous navigation)
    // Use generic page name format for consistency - actual page titles handled in AdminView
    const backTableEntry: AdminBackTableDto = {
      name: `Page ${adminState.pageNum}`, // Simple format, let AdminView handle actual titles
      value: '',  // Value should be empty per BackTable requirements
      segment: adminState.segment,
      pageNum: adminState.pageNum // This must exactly match AdminSegment page.pageNum
    };
    
    // Only add if not already the last entry (avoid duplicates)
    const lastEntry = adminState.backTable[adminState.backTable.length - 1];
    if (!lastEntry || lastEntry.segment !== adminState.segment || lastEntry.pageNum !== adminState.pageNum) {
      adminState.backTable.push(backTableEntry);
    }
    
    if (currentPage < totalPagesInCurrentSegment) {
      // Move to next page in current segment
      adminState.pageNum = (currentPage + 1).toString();
    } else {
      // Current segment complete, move to next segment
      const segments = adminState.segmentList.split(',').map(s => s.trim()).filter(s => s);
      const currentSegmentIndex = segments.indexOf(adminState.segment);
      
      if (currentSegmentIndex >= 0 && currentSegmentIndex < segments.length - 1) {
        // Move to next segment
        adminState.segment = segments[currentSegmentIndex + 1];
        adminState.pageNum = '1'; // Reset to first page of new segment
      } else {
        // All segments complete - assessment finished
        adminState.status = 'COMPLETED';
      }
    }

    await this.saveAdminState(adminState);
    return adminState;
  }

  /**
   * Navigate to previous page - handles branching logic by removing specific variable data and BackTable entry
   */
  async navigateToPreviousPage(
    profileId: string,
    sourceId: string,
    currentAdminState?: AdminStateDto
  ): Promise<AdminStateDto | null> {
    // Use provided state or load from storage
    const adminState = currentAdminState || await this.loadAdminState(profileId, sourceId);
    if (!adminState) return null;

    // Work directly with backTable (camelCase only)
    if (!adminState.backTable || !Array.isArray(adminState.backTable)) {
      return adminState;
    }
    
    const backTableArray = adminState.backTable;
    
    // Check if backTable has more than 1 item (need previous state to navigate to)
    if (backTableArray.length <= 1) {
      return adminState;
    }

    // Get the last BackTable entry (current position) to remove its data
    const lastEntry = backTableArray[backTableArray.length - 1];
    
    // Get the second-to-last BackTable entry (destination)
    const previousEntry = backTableArray[backTableArray.length - 2];
    
    if (lastEntry && previousEntry) {
      // Work directly with data array (camelCase only)
      if (!adminState.data || !Array.isArray(adminState.data)) {

        return adminState;
      }
      
      const dataArray = adminState.data;
      
      // Clean up data array - remove entries from current page and any later pages/segments
      // Use segment and pageNum for data cleanup, not the name field
      const updatedData = dataArray.filter((dataEntry: any) => {
        const entrySegment = dataEntry.segment;
        const entryPageNum = dataEntry.pageNum;
        const targetSegment = previousEntry.segment;
        const targetPageNum = previousEntry.pageNum;
        
        // Keep data if it's from target page or earlier
        // This logic should match the cleanup in AdminView goToPreviousPage
        if (entrySegment === targetSegment && entryPageNum === targetPageNum) {
          return true; // Keep target page data
        }
        
        // Remove data from later positions (this is simple since we're going back one step)
        if (entrySegment === lastEntry.segment && entryPageNum === lastEntry.pageNum) {
          return false; // Remove current page data
        }
        
        return true; // Keep other data
      });
      
      // Update the data array
      adminState.data = updatedData;

      // Set segment and pageNum from the previous BackTable entry (adminState as source of truth)
      adminState.segment = previousEntry.segment;
      adminState.pageNum = previousEntry.pageNum;
      
      // Remove the last backTable item from the actual array
      backTableArray.pop();
    }

    await this.saveAdminState(adminState);
    return adminState;
  }

  /**
   * Navigate to next segment
   */
  async navigateToNextSegment(
    accountId: string,
    profileId: string,
    sourceId: string,
    totalSegments: number
  ): Promise<AdminStateDto | null> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    if (!adminState) return null;

    const currentSegment = parseInt(adminState.segment) || 1;
    if (currentSegment < totalSegments) {
      adminState.segment = (currentSegment + 1).toString();
      adminState.pageNum = '1'; // Reset to first page of new segment
      await this.saveAdminState(adminState);
    }

    return adminState;
  }

  /**
   * Navigate to previous segment
   */
  async navigateToPreviousSegment(
    accountId: string,
    profileId: string,
    sourceId: string
  ): Promise<AdminStateDto | null> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    if (!adminState) return null;

    const currentSegment = parseInt(adminState.segment) || 1;
    if (currentSegment > 1) {
      adminState.segment = (currentSegment - 1).toString();
      adminState.pageNum = '1'; // Reset to first page of previous segment
      await this.saveAdminState(adminState);
    }

    return adminState;
  }

  /**
   * Complete assessment
   */
  async completeAssessment(
    accountId: string,
    profileId: string,
    sourceId: string
  ): Promise<AdminStateDto | null> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    if (!adminState) return null;

    adminState.status = 'COMPLETED';
    adminState.modified = new Date().toISOString();
    
    await this.saveAdminState(adminState);
    return adminState;
  }

  /**
   * Delete AdminState
   */
  async deleteAdminState(
    accountId: string,
    profileId: string,
    sourceId: string
  ): Promise<void> {
    try {
      const key = this.generateKey(accountId, profileId, sourceId);
      
      await this.storage.removeItem(key);
      this.cache.delete(key);
      
      console.log(`🗑️ Deleted AdminState: ${accountId}/${profileId}/${sourceId}`);
    } catch (error) {
      console.error('Failed to delete AdminState:', error);
      throw error;
    }
  }

  /**
   * Create assessment session (runtime state)
   */
  createAssessmentSession(
    adminState: AdminStateDto,
    availableSegments: string[]
  ): AdminAssessmentSession {
    const currentSegmentIndex = parseInt(adminState.segment) - 1;
    const currentPageIndex = parseInt(adminState.pageNum) - 1;
    
    const session: AdminAssessmentSession = {
      adminState,
      currentSegmentIndex,
      currentPageIndex,
      availableSegments,
      isComplete: adminState.status === 'COMPLETED',
      lastModified: adminState.modified
    };

    const sessionKey = `${adminState.profileId}_${adminState.sourceId}`;
    this.sessionCache.set(sessionKey, session);
    
    return session;
  }

  /**
   * Get navigation info
   */
  getNavigationInfo(
    adminState: AdminStateDto,
    totalSegments: number,
    totalPagesInCurrentSegment: number
  ): AdminSegmentNavigation {
    const currentSegment = parseInt(adminState.segment) || 1;
    const currentPageNum = parseInt(adminState.pageNum) || 1;
    
    return {
      currentSegment: adminState.segment,
      currentPageNum: adminState.pageNum,
      totalSegments,
      totalPagesInSegment: totalPagesInCurrentSegment,
      canGoToNextSegment: currentSegment < totalSegments,
      canGoToPreviousSegment: currentSegment > 1,
      canGoToNextPage: currentPageNum < totalPagesInCurrentSegment,
      canGoToPreviousPage: currentPageNum > 1
    };
  }

  /**
   * Restart assessment - Reset to first page and clear temporary data
   */
  async restartAssessment(
    profileId: string,
    sourceId: string
  ): Promise<AdminStateDto> {
    const adminState = await this.loadAdminState(profileId, sourceId);
    
    if (!adminState) {
      throw new Error(`AdminState not found for ${profileId}/${sourceId}`);
    }
    
    // Reset to first segment from segmentList and page
    const segments = adminState.segmentList.split(',').map(s => s.trim()).filter(s => s);
    adminState.segment = segments.length > 0 ? segments[0] : '1';
    adminState.pageNum = '1';
    adminState.status = 'IN_PROGRESS';
    
    // Clear temporary data arrays but keep prefill data
    adminState.data = [];
    adminState.backTable = [];
    // Keep prefill data as it may contain important baseline information
    
    // Update modified timestamp
    adminState.modified = new Date().toISOString();
    
    // Save updated state
    await this.saveAdminState(adminState);
    
    console.log(`♻️ Restarted AdminState: ${adminState.recId}`);
    return adminState;
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.cache.clear();
    this.sessionCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      adminStateCache: this.cache.size,
      sessionCache: this.sessionCache.size,
      adminStateKeys: Array.from(this.cache.keys()),
      sessionKeys: Array.from(this.sessionCache.keys())
    };
  }

  // Private helper methods

  private generateKey(profileId: string, sourceId: string, recId?: string): string {
    if (recId) {
      return `admin_state_${profileId}_${sourceId}_${recId}`;
    }
    return `admin_state_${profileId}_${sourceId}`;
  }

  private generateKeyForAdminState(adminState: AdminStateDto): string {
    return `admin_state_${adminState.profileId}_${adminState.sourceId}_${adminState.recId}`;
  }

  private async updateKeyRegistry(key: string): Promise<void> {
    try {
      const keyRegistry = await this.storage.getItem('admin_state_key_registry');
      const keys: string[] = keyRegistry ? JSON.parse(keyRegistry) : [];
      
      if (!keys.includes(key)) {
        keys.push(key);
        await this.storage.setItem('admin_state_key_registry', JSON.stringify(keys));
      }
    } catch (error) {
      console.error('Failed to update key registry:', error);
    }
  }

  private generateStorageKey(profileId: string, sourceId: string): string {
    const key = `${profileId}_${sourceId}`;
    console.log(`🔑 AdminStateStorage: Generated storage key: ${key}`);
    return key;
  }

  private generateRecId(): string {
    return `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async getAllAdminStateKeys(): Promise<string[]> {
    try {
      // Since StorageInterface doesn't have getAllKeys, we'll maintain a key registry
      const keyRegistry = await this.storage.getItem('admin_state_key_registry');
      if (keyRegistry) {
        return JSON.parse(keyRegistry);
      }
      return [];
    } catch (error) {
      console.error('Failed to get admin state keys:', error);
      return [];
    }
  }
}